import React from 'react';

/**
 * Componente que renderiza a barra de progresso visual dos status dos projetos.
 * Cada status é uma seção colorida da barra, com largura proporcional à sua porcentagem.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos, cada um representando um status de projeto.
 * @param {number} props.totalProjects - O número total de projetos.
 * @returns {React.Component|null} A barra de status ou nulo se não houver dados.
 */
// ⚡ Bolt: Wrapped in React.memo() to prevent unnecessary re-renders when parent ProjectsPage state (like searchTerm) changes.
const ProjectStatusBar = React.memo(({ data, totalProjects }) => {
    if (!data || totalProjects === 0) return null;

    return (
        <div className="h-3 w-full rounded-full overflow-hidden flex bg-slate-200 dark:bg-slate-700">
            {data.map(item => {
                // Não renderiza segmentos com valor zero
                if (item.value === 0) return null;
                const widthPercentage = (item.value / totalProjects) * 100;
                return (
                    <div
                        key={item.name}
                        className="h-full"
                        style={{
                            width: `${widthPercentage}%`,
                            backgroundColor: item.color,
                        }}
                        title={`${item.name}: ${item.percentage.toFixed(1)}%`}
                    />
                );
            })}
        </div>
    );
});

/**
 * Componente que renderiza a legenda para a barra de status dos projetos.
 * Mostra o nome, a cor e a porcentagem de cada status.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos, cada um representando um status de projeto.
 * @returns {React.Component|null} A legenda ou nulo se não houver dados.
 */
const ProjectStatusLegend = React.memo(({ data }) => {
    if (!data || data.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
            {/* Filtra para mostrar apenas status com projetos */}
            {data.filter(item => item.value > 0).map(item => (
                <div key={item.name} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                    <span className="text-slate-600 dark:text-slate-400 font-medium">{item.name}:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{item.percentage.toFixed(1)}%</span>
                </div>
            ))}
        </div>
    );
});

/**
 * Componente principal que agrupa a barra de status e a legenda.
 * @param {object} props - Propriedades do componente.
 * @param {Array} props.data - Array de objetos com os dados de status dos projetos.
 * @returns {React.Component} O componente completo de exibição de status.
 */
const ProjectStatusDisplay = React.memo(({ data }) => {
    const totalProjects = data ? data.reduce((acc, item) => acc + item.value, 0) : 0;
    
    // Mostra uma mensagem se não houver projetos
    if (totalProjects === 0) {
        return <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-2">Nenhum projeto para exibir o status.</p>;
    }

    return (
        <div className="w-full">
            <ProjectStatusBar data={data} totalProjects={totalProjects}/>
            <ProjectStatusLegend data={data} />
        </div>
    );
});

// ⚡ Bolt: Wrapped in React.memo() to prevent expensive re-renders during text input in search bar
export default React.memo(ProjectStatusDisplay);
